import locationUSCityStateZipInternationalCityModelValidator from 'forms/shared/validators/models/location/models/usCityStateZipInternationalCity';
import { DEFAULT_LATEST, LAST_FIVE_YEARS, LAST_SEVEN_YEARS } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import { NOT_WITHIN_LAST_SEVEN_YEARS } from 'constants/errors';
import { SafeDateTime } from 'helpers/types';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { isPVQForm } from 'helpers/form';
import { foreignBusinessAffairsAgencyOptions, foreignBusinessAffairsFamilyOptions } from 'constants/enums/foreignBusinessOptions';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { hasYesOrNo } from 'models/validate';
import foreignBusinessSubsequentContactsItemValidator from './fields';
import { ForeignBusinessContactItemModel } from '../types';

const foreignBusinessContactItemValidator: ValidateJSModel<ForeignBusinessContactItemModel> = {
  WhoContacted: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: foreignBusinessAffairsFamilyOptions,
          },
        },
      };
    }

    return {};
  },

  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.WhoContacted?.value !== 'Myself') {
        return { presence: true, model: { validator: pvqNameModelValidator } };
      }

      return {};
    }

    return { presence: true, model: { validator: nameModelValidator } };
  },

  LocationNotApplicable: {},

  Location: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.LocationNotApplicable?.applicable === false) {
        return {};
      }

      return {
        presence: true,
        location: {
          validator: pvqLocationModelValidator,
          requireStreet: true,
        }
      };
    }

    return {
      presence: true,
      location: { validator: locationUSCityStateZipInternationalCityModelValidator }
    };
  },

  Date: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        date: {
          earliest: setToStartOfMonth(getSafeDateTime(LAST_FIVE_YEARS)),
          tooEarly: 'error.beforeDuration',
          latest: DEFAULT_LATEST,
        },
      };
    }

    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && setToStartOfMonth(getSafeDateTime(getSafeDateTime(options?.applicantBirthdate)?.datetime?.plus({ years: 18 })));
    let earliestDate = setToStartOfMonth(getSafeDateTime(LAST_SEVEN_YEARS));
    let earlyError = NOT_WITHIN_LAST_SEVEN_YEARS;

    if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid
      && isSafeDateLater(earliestDate, applicantEighteenthBirthdate)) {
      earliestDate = applicantEighteenthBirthdate;
      earlyError = 'error.beforeDuration';
    }

    return {
      presence: true,
      date: {
        earliest: earliestDate.datetime,
        tooEarly: earlyError,
        latest: DEFAULT_LATEST,
      },
    };
  },

  Governments: { hasValue: true, country: { excludeUnitedStates: true } },

  Establishment: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return { hasValue: true };
  },

  Representatives: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return { hasValue: true };
  },

  Purpose: { hasValue: true },

  SubsequentContacts: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return {
      presence: true,
      branchCollection: { validator: foreignBusinessSubsequentContactsItemValidator }
    };
  },

  AgencyType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: foreignBusinessAffairsAgencyOptions,
          },
        },
      };
    }

    return {};
  },

  AgencyTypeOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.AgencyType?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  AgencyNameNotApplicable: {},

  AgencyName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.AgencyNameNotApplicable?.applicable !== false) {
      return { hasValue: true };
    }

    return {};
  },

  RepresentativeNameNotApplicable: {},

  RepresentativeName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.RepresentativeNameNotApplicable?.applicable !== false) {
      return {
        presence: true,
        model: {
          validator: pvqNameModelValidator,
          requireMiddleName: false
        }
      };
    }

    return {};
  },

  HasFuturePlansToContact: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },

  HasFuturePlansToContactExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.HasFuturePlansToContact?.value === 'Yes') {
      return { hasValue: true };
    }

    return {};
  },

};

export default foreignBusinessContactItemValidator;
